import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Utensils, 
  Info, 
  Calculator,
  BookOpen,
  Cookie,
  Pill,
  Lightbulb
} from 'lucide-react';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

function Nutrition() {
  const [activeTab, setActiveTab] = useState('foods');
  const [foods, setFoods] = useState([]);
  const [treats, setTreats] = useState([]);
  const [supplements, setSupplements] = useState([]);
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    type: '',
    life_stage: '',
    size_category: '',
    special_diet: false,
    search: ''
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, filters]);

  const fetchData = async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case 'foods':
          const foodParams = new URLSearchParams();
          Object.entries(filters).forEach(([key, value]) => {
            if (value) foodParams.append(key, value);
          });
          const foodsRes = await api.get(`/nutrition/foods?${foodParams}`);
          setFoods(foodsRes.data);
          break;
        case 'treats':
          const treatsRes = await api.get('/nutrition/treats');
          setTreats(treatsRes.data);
          break;
        case 'supplements':
          const supplementsRes = await api.get('/nutrition/supplements');
          setSupplements(supplementsRes.data);
          break;
        case 'tips':
          const tipsRes = await api.get('/nutrition/tips');
          setTips(tipsRes.data);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error fetching nutrition data:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'foods', label: 'Dog Foods', shortLabel: 'Foods', icon: Utensils },
    { id: 'treats', label: 'Treats', shortLabel: 'Treats', icon: Cookie },
    { id: 'supplements', label: 'Supplements', shortLabel: 'Supplements', icon: Pill },
    { id: 'tips', label: 'Nutrition Tips', shortLabel: 'Tips', icon: Lightbulb }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-4 sm:py-8 overflow-x-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
            Pet Nutrition Center
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600">
            Everything you need to know about feeding your furry friend
          </p>
        </div>

        <div className="mb-8 -mx-4 sm:mx-0">
          <div className="overflow-x-auto px-4 sm:px-0">
            <div className="flex justify-center">
              <div className="inline-flex rounded-lg bg-white shadow p-1 gap-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                        flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 rounded-md transition-all whitespace-nowrap text-xs sm:text-sm
                        ${activeTab === tab.id
                          ? 'bg-purple-600 text-white shadow-md'
                          : 'text-gray-600 hover:text-gray-900'
                        }
                      `}
                    >
                      <Icon className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                      <span className="hidden sm:inline">{tab.label}</span>
                      <span className="sm:hidden">{tab.shortLabel}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {activeTab === 'foods' && (
          <div className="mb-6 bg-white rounded-lg shadow p-3 sm:p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2 sm:gap-3">
              <input
                type="text"
                placeholder="Search foods..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent w-full"
              />
              <select
                value={filters.type}
                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 w-full"
              >
                <option value="">All Types</option>
                <option value="dry">Dry Food</option>
                <option value="wet">Wet Food</option>
                <option value="raw">Raw Food</option>
                <option value="freeze-dried">Freeze-Dried</option>
              </select>
              <select
                value={filters.life_stage}
                onChange={(e) => setFilters({ ...filters, life_stage: e.target.value })}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 w-full"
              >
                <option value="">All Life Stages</option>
                <option value="puppy">Puppy</option>
                <option value="adult">Adult</option>
                <option value="senior">Senior</option>
              </select>
              <select
                value={filters.size_category}
                onChange={(e) => setFilters({ ...filters, size_category: e.target.value })}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 w-full"
              >
                <option value="">All Sizes</option>
                <option value="small">Small Breed</option>
                <option value="medium">Medium Breed</option>
                <option value="large">Large Breed</option>
                <option value="giant">Giant Breed</option>
              </select>
              <label className="flex items-center space-x-2 px-2 sm:px-3 py-2">
                <input
                  type="checkbox"
                  checked={filters.special_diet}
                  onChange={(e) => setFilters({ ...filters, special_diet: e.target.checked })}
                  className="rounded text-purple-600 focus:ring-purple-500 flex-shrink-0"
                />
                <span className="text-xs sm:text-sm whitespace-nowrap">Special Diet Only</span>
              </label>
            </div>
          </div>
        )}

        <div className="mb-6 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
          <Link
            to="/app/nutrition/calculator"
            className="flex items-center justify-center space-x-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm sm:text-base"
          >
            <Calculator className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
            <span className="whitespace-nowrap">Feeding Calculator</span>
          </Link>
          <Link
            to="/app/nutrition/meal-plans"
            className="flex items-center justify-center space-x-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
          >
            <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
            <span className="whitespace-nowrap">My Meal Plans</span>
          </Link>
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
            {activeTab === 'foods' && foods.map((food) => (
              <Link
                key={food.id}
                to={`/app/nutrition/food/${food.id}`}
                className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow p-4 sm:p-5 lg:p-6"
              >
                <div className="flex justify-between items-start mb-3 gap-2">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex-1">{food.name}</h3>
                  <span className={`px-2 py-1 text-xs rounded-full flex-shrink-0 ${
                    food.type === 'dry' ? 'bg-orange-100 text-orange-800' :
                    food.type === 'wet' ? 'bg-blue-100 text-blue-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {food.type}
                  </span>
                </div>
                <p className="text-xs sm:text-sm text-gray-600 mb-2">{food.brand}</p>
                {food.special_diet && (
                  <p className="text-xs sm:text-sm text-purple-600 font-medium mb-2">{food.diet_type}</p>
                )}
                <div className="flex justify-between text-xs sm:text-sm text-gray-500 flex-wrap gap-1">
                  <span>{food.calories_per_cup} cal/cup</span>
                  <span>{food.protein_percentage}% protein</span>
                </div>
                <div className="mt-3 sm:mt-4 flex items-center text-purple-600">
                  <Info className="w-4 h-4 mr-1 flex-shrink-0" />
                  <span className="text-xs sm:text-sm">View Details</span>
                </div>
              </Link>
            ))}

            {activeTab === 'treats' && treats.map((treat) => (
              <div key={treat.id} className="bg-white rounded-lg shadow p-4 sm:p-5 lg:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">{treat.name}</h3>
                <p className="text-xs sm:text-sm text-gray-600 mb-2">{treat.brand}</p>
                <div className="mb-3">
                  <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                    treat.suitable_for_training ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {treat.type}
                  </span>
                </div>
                <p className="text-xs sm:text-sm text-gray-500 mb-2">{treat.calories_per_piece} calories/piece</p>
                <p className="text-xs sm:text-sm text-gray-700">{treat.description}</p>
              </div>
            ))}

            {activeTab === 'supplements' && supplements.map((supplement) => (
              <div key={supplement.id} className="bg-white rounded-lg shadow p-4 sm:p-5 lg:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">{supplement.name}</h3>
                <span className="inline-block px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800 mb-3">
                  {supplement.type}
                </span>
                <p className="text-xs sm:text-sm text-gray-700 mb-3">{supplement.purpose}</p>
                <div className="border-t pt-3">
                  <p className="text-xs text-gray-600 break-words">
                    <strong>Dosage:</strong> {supplement.dosage_instructions}
                  </p>
                  {supplement.precautions && (
                    <p className="text-xs text-red-600 mt-2 break-words">
                      <strong>Precautions:</strong> {supplement.precautions}
                    </p>
                  )}
                </div>
              </div>
            ))}

            {activeTab === 'tips' && tips.map((tip) => (
              <div key={tip.id} className="bg-white rounded-lg shadow p-4 sm:p-5 lg:p-6">
                <div className="flex items-start space-x-2 sm:space-x-3">
                  <Lightbulb className={`w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0 ${
                    tip.importance_level === 'essential' ? 'text-red-500' :
                    tip.importance_level === 'important' ? 'text-yellow-500' :
                    'text-green-500'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">{tip.title}</h3>
                    <p className="text-xs sm:text-sm text-gray-700 break-words">{tip.content}</p>
                    <div className="mt-3">
                      <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                        tip.importance_level === 'essential' ? 'bg-red-100 text-red-800' :
                        tip.importance_level === 'important' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {tip.importance_level}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Nutrition;